import { useEffect } from "react";

import Divider from "ui-components/Divider";
import Transition from "ui-components/Transition";

export interface Props {
  children: React.ReactNode;
  id: string;
  ariaLabel?: string;
  show: boolean;
  handleClose: () => any;
  title?: React.ReactNode;
}

function Modal({ children, title, id, ariaLabel, show, handleClose }: Props) {
  // close the modal if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (keyCode === 27) {
        handleClose();
      }
    };
    document.addEventListener("keydown", keyHandler);

    return () => document.removeEventListener("keydown", keyHandler);
  });

  return (
    <>
      {/* Modal backdrop */}
      <Transition
        className="fixed inset-0 z-50 bg-gray-600 bg-opacity-75 transition-opacity blur"
        show={show}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
      />

      {/* Modal dialog */}
      <Transition
        id={id}
        className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center transform px-4 sm:px-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabel || id}
        show={show}
        enter="transition ease-out duration-200"
        enterStart="opacity-0 scale-95"
        enterEnd="opacity-100 scale-100"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100 scale-100"
        leaveEnd="opacity-0 scale-95"
        onClick={(e: React.MouseEvent) => {
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
      >
        <>
          <div className="bg-white overflow-auto max-w-6xl w-full max-h-full m-4 p-4">
            <div className="modal-close absolute top-0 right-0 cursor-pointer flex flex-col items-center mt-4 mr-4 text-white text-sm z-50">
              <svg
                className="fill-current text-white"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                onClick={() => handleClose()}
              >
                <path d="M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z"></path>
              </svg>
              <span className="text-sm" onClick={() => handleClose()}>
                (Esc)
              </span>
            </div>
            <div className="p-2 m-2">
              {title && (
                <>
                  <div className="flex justify-between items-center">
                    <div className="h4">{title}</div>
                    <div
                      className="modal-close cursor-pointer z-50"
                      onClick={() => handleClose()}
                    >
                      <svg
                        className="fill-current text-black"
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                      >
                        <path d="M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z"></path>
                      </svg>
                    </div>
                  </div>
                  <Divider />
                </>
              )}
              {children}
            </div>
          </div>
        </>
      </Transition>
    </>
  );
}

export default Modal;
